import type { Feature } from 'geojson'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  FLOAT_LENGTH_FEET_TYPE,
  INT_DIAM_INCHES_TYPE,
  analyticsGeoCoordPair,
  isMapFeaturePropPresent,
  orderMapFeaturePropsForPopup,
  PHONE_TYPE,
} from '@buxus/shared'
import { MapCoordinateCopyStrip } from './MapCoordinateCopyStrip'
import {
  resolvePipelineViewOperatorTarget,
  type PipelineMapOperatorDirectoryEntry,
} from './mapPipelinePopupDom'
import {
  trackButtonPressed,
  trackPhonePressed,
  type ScreenInteractionContext,
} from './platform/analytics/interactionAnalytics'

function formatPropValue(
  prop: { type: number; valueSuffix?: string },
  raw: unknown,
): string {
  if (raw === null || raw === undefined) return ''
  if (prop.type === PHONE_TYPE && typeof raw === 'number') return String(raw)
  if (prop.type === FLOAT_LENGTH_FEET_TYPE && typeof raw === 'number') {
    const v = raw > 0 && raw < 1 ? Math.round(raw * 10) / 10 : Math.trunc(raw)
    return `${v}${prop.valueSuffix ?? ''}`
  }
  if (prop.type === INT_DIAM_INCHES_TYPE && typeof raw === 'number') {
    return `${Math.trunc(raw)}${prop.valueSuffix ?? ''}`
  }
  return `${raw}${prop.valueSuffix ?? ''}`
}

type MapPipelineBubbleProps = {
  features: Feature[]
  selectedIndex: number
  /** Map click used to query pipeline features (approximate, not on-pipe geometry). */
  tapPoint?: { lng: number; lat: number } | null
  onSegmentNavigate: (nextIndex: number, direction: 'previous' | 'next') => void
  onClose: () => void
  onViewOperator?: (operatorId: string) => void
  operatorDirectory?: readonly PipelineMapOperatorDirectoryEntry[]
  analytics?: ScreenInteractionContext
  mapRole?: string
}

export function MapPipelineBubble({
  features,
  selectedIndex,
  tapPoint,
  onSegmentNavigate,
  onClose,
  onViewOperator,
  operatorDirectory,
  analytics,
  mapRole,
}: MapPipelineBubbleProps) {
  const feat = features[selectedIndex]
  const propsObj = ((feat?.properties ?? {}) as Record<string, unknown>) ?? {}
  const viewOperatorId =
    onViewOperator && operatorDirectory?.length
      ? resolvePipelineViewOperatorTarget(propsObj, operatorDirectory)
      : null

  const propsWrapRef = useRef<HTMLDivElement>(null)
  const orderedProps = orderMapFeaturePropsForPopup()
  const [propsOverflows, setPropsOverflows] = useState(false)
  const [propsAtBottom, setPropsAtBottom] = useState(false)

  const syncPropsScrollState = useCallback(() => {
    const el = propsWrapRef.current
    if (!el) return
    const overflows = el.scrollHeight > el.clientHeight + 1
    setPropsOverflows(overflows)
    setPropsAtBottom(
      !overflows || el.scrollHeight - el.scrollTop - el.clientHeight < 4,
    )
  }, [])

  useEffect(() => {
    let cancelled = false

    const measure = () => {
      if (cancelled) return
      syncPropsScrollState()
    }

    measure()
    const raf = requestAnimationFrame(() => requestAnimationFrame(measure))

    const el = propsWrapRef.current
    if (!el) {
      cancelAnimationFrame(raf)
      return () => {
        cancelled = true
      }
    }

    const observer = new ResizeObserver(() => measure())
    observer.observe(el)
    const inner = el.querySelector('.map-pipeline-popup-props-inner')
    if (inner) observer.observe(inner)
    el.addEventListener('scroll', syncPropsScrollState, { passive: true })
    el.focus({ preventScroll: true })
    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      observer.disconnect()
      el.removeEventListener('scroll', syncPropsScrollState)
    }
  }, [selectedIndex, features, syncPropsScrollState])

  const propsWrapClassName = [
    'map-pipeline-popup-props-wrap',
    propsOverflows && !propsAtBottom ? 'map-pipeline-popup-props-wrap--more' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className="map-pipeline-popup map-pipeline-popup--segment"
      role="dialog"
      aria-label="Pipeline segment"
    >
      <div className="map-pipeline-popup-content">
        {features.length > 1 ? (
          <div className="map-pipeline-popup-nav">
            <button
              type="button"
              className="map-pipeline-popup-icon-btn"
              disabled={selectedIndex <= 0}
              aria-label="Previous segment"
              onClick={() =>
                onSegmentNavigate(Math.max(0, selectedIndex - 1), 'previous')
              }
            >
              <span aria-hidden="true">&#10094;</span>
            </button>
            <span className="map-pipeline-popup-nav-label">
              {selectedIndex + 1} of {features.length}
            </span>
            <button
              type="button"
              className="map-pipeline-popup-icon-btn"
              disabled={selectedIndex + 1 >= features.length}
              aria-label="Next segment"
              onClick={() =>
                onSegmentNavigate(
                  Math.min(features.length - 1, selectedIndex + 1),
                  'next',
                )
              }
            >
              <span aria-hidden="true">&#10095;</span>
            </button>
          </div>
        ) : null}

        {tapPoint ? (
          <MapCoordinateCopyStrip
            lng={tapPoint.lng}
            lat={tapPoint.lat}
            onCopySuccess={() => {
              if (!analytics) return
              trackButtonPressed({
                ...analytics,
                button: 'Copy coordinates',
                component: 'MapPipelinePopup',
                mapRole,
                ...(analyticsGeoCoordPair(tapPoint.lat, tapPoint.lng) ?? {}),
              })
            }}
          />
        ) : null}

        <div
          ref={propsWrapRef}
          tabIndex={-1}
          className={propsWrapClassName}
          aria-label="Pipeline segment details"
        >
          <dl className="map-pipeline-popup-props">
            <div className="map-pipeline-popup-props-inner" key={selectedIndex}>
            {feat?.id !== undefined && feat.id !== null ? (
              <div className="map-pipeline-popup-prop-row">
                <dt>ID</dt>
                <dd>{String(feat.id)}</dd>
              </div>
            ) : null}
            {orderedProps.map((prop) => {
              const raw = propsObj[prop.key]
              if (
                !Object.prototype.hasOwnProperty.call(propsObj, prop.key) ||
                !isMapFeaturePropPresent(raw)
              ) {
                return null
              }
              const display = formatPropValue(prop, raw)
              if (!display.trim()) return null
              return (
                <div key={prop.key} className="map-pipeline-popup-prop-row">
                  <dt>{prop.label}</dt>
                  <dd>
                    {prop.type === PHONE_TYPE ? (
                      <a
                        className="map-pipeline-popup-tel"
                        href={`tel:${encodeURIComponent(`${raw}`.replace(/\s/g, ''))}`}
                        onClick={() => {
                          if (analytics) {
                            trackPhonePressed({
                              ...analytics,
                              phone: `${raw}`.replace(/\s/g, ''),
                              contact: prop.label,
                            })
                          }
                        }}
                      >
                        {display}
                      </a>
                    ) : (
                      display
                    )}
                  </dd>
                </div>
              )
            })}
            </div>
          </dl>
        </div>
      </div>

      <div className="map-pipeline-popup-actions">
        {viewOperatorId && onViewOperator ? (
          <button
            type="button"
            className="map-pipeline-popup-btn primary"
            onClick={() => onViewOperator(viewOperatorId)}
          >
            View Operator
          </button>
        ) : null}
        <button type="button" className="map-pipeline-popup-btn primary" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  )
}
